/* eslint-disable jsx-a11y/anchor-is-valid, no-confusing-arrow */
import React, {
  useState, useMemo, useEffect
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { I18n, Translate } from 'src/translations/i18n';
import { Link } from 'react-router-dom';
import {
  Collapse, Divider, Drawer, List, ListItem, ListItemIcon, ListItemText,
  Switch, ListItemSecondaryAction, IconButton, withStyles, TextField, Box
} from '@material-ui/core';
import {
  isEmpty, unnest, contains, pluck
} from 'ramda';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import classNames from 'classnames';

import { selectShowMenuSearch } from 'src/store/selectors/personalConfiguration';

import 'src/style/customScrollBar.css';
import cube from 'src/images/logo-CompuAccess-cube.svg';
import logoText from 'src/images/caTekst.svg';
import { selectIsModuleEnabled } from 'src/store/selectors/configurations';
import { selectUserId } from 'src/store/selectors/userInfo';
import Utils from 'src/utils';
import { SORT_ORDER } from 'src/constants/workTimeService';
import { readSidebarAutoClose, writeSidebarAutoClose } from '../app/sidebarState';
import WorkShiftButton from '../Buttons/WorkShiftButton';

const listItemText = { color: 'inherit' };

const childOpen = (children, selected) => {
  if (isEmpty(children)) return false;
  if (contains(selected, children)) return true;
  return childOpen(unnest(pluck('subPages', children)), selected);
};

const drawerWidth = 240;

const styles = (theme) => ({
  drawerContent: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: theme.palette.navDrawer.backGround,
    paddingBottom: 54
  },
  drawerPaper: {
    backgroundColor: theme.palette.navDrawer.backGround,
    position: 'relative',
    overflow: 'visible',
    whiteSpace: 'nowrap',
    width: drawerWidth,
    borderRight: 'none',
    transition: theme.transitions.create('all', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    }),
    '@media print': {
      display: 'none'
    }
  },
  drawerPaperClose: {
    transition: theme.transitions.create('all', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    width: theme.spacing(7),
    [theme.breakpoints.up('sm')]: {
      width: theme.spacing(9)
    }
  },
  navContainer: {
    height: 'calc(100% - 150)',
    overflowY: 'scroll',
    overflowX: 'hidden',
    paddingTop: 0,
    '&::-webkit-scrollbar': {
      width: 5
    },
    '&::-webkit-scrollbar-track': {
      boxShadow: 'none',
      borderRadius: 3
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme.palette.navDrawer.backGround,
      borderRadius: 5
    },
    '&:hover': {
      '&::-webkit-scrollbar-thumb': {
        background: theme.palette.navDrawer.scrollColor
      }
    }
  },
  toolbarLink: {
    boxShadow: theme.palette.navDrawer.toolbarBoxShadow,
    zIndex: 1
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: '0 8px',
    ...theme.mixins.toolbar,
    minHeight: 69
  },
  drawerSecondaryActionOpen: {
    opacity: 100,
    transition: theme.transitions.create('all', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  },
  drawerSecondaryActionClose: {
    opacity: 0,
    transition: theme.transitions.create('all', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    })
  },
  divider: {
    backgroundColor: theme.palette.navDrawer.dividerColor
  },
  selectedListItem: {
    borderRightWidth: 3,
    borderRightColor: theme.palette.navDrawer.itemSelectedBorder,
    borderRightStyle: 'solid',
    color: '#FFF!important',
    backgroundColor: `${theme.palette.navDrawer.itemSelected}!important`
  },
  listItem: {
    paddingTop: 0,
    paddingBottom: 0,
    color: 'white',
    '&:hover': {
      backgroundColor: theme.palette.navDrawer.itemHover
    },
    transition: 'padding-left 0.3s'
  },
  link: {
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    width: '100%',
    padding: '11px 0',
    textDecoration: 'none!important'
  },
  listRoot: {
    overflowX: 'hidden'
  },
  iconStyle: {
    height: 18,
    width: 18,
    color: 'white',
    marginRight: 0,
    display: 'flex',
    justifyContent: 'center',
    minWidth: 18,
    transition: 'min-width 0.3s'
  },
  closeIcon: {
    minWidth: 48
  },
  parentStyle: {
    paddingRight: 0,
    paddingLeft: 12,
    color: 'white',
    display: 'inline-flex'
  },
  search: {
    margin: 12,
    color: '#FFF'
  },
  inputLabel: {
    color: '#FFF!important'
  },
  inputOutline: {
    borderColor: '#FFF!important'
  },
  input: {
    color: theme.palette.navBarInput
  },
  shadowEl: {
    boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)',
    zIndex: -1,
    position: 'absolute',
    top: 64,
    left: 0,
    right: 0,
    bottom: 0
  },
  autoHideSwitch: {
    position: 'absolute',
    left: 0,
    bottom: 0,
    color: 'white',
    marginTop: 'auto',
    paddingLeft: 7
  }
});

const routesSortFn = Utils.sort.getSortFn(SORT_ORDER.ASC, 'name');

function NavigationDrawer({
  showMenuSearch, open, classes, selectedNav, routes, onOpenDrawer, onCloseDrawer,
  isManagerMode, isRegisterWorkOnComputerModuleEnabled, userId
}) {
  const [openTabs, setOpenTabs] = useState({});
  const [enableAutoToggle, setEnableAutoToggle] = useState(false);
  const [search, setSearch] = useState('');

  // Restore the persisted "auto-close" switch once the user id is known (reading the
  // correct per-user key, not the anonymous fallback), so a reload keeps the switch in
  // the same position (and the drawer keeps opening on hover).
  useEffect(() => {
    if (userId != null) {
      setEnableAutoToggle(readSidebarAutoClose(userId));
    }
  }, [userId]);

  const handleSetSearch = ({ target }) => setSearch(target.value);

  const renderNavItems = (list) => {
    const isSelected = (navTarget) => selectedNav?.link === navTarget;
    return list.map((route) => (
      <React.Fragment key={route.selectedTabId}>
        {route.subPages?.length
          ? (
            <>
              {renderSingleNav(route, isSelected(route.link), false, true)}
              <Collapse in={openTabs[route.selectedTabId] || false}>
                <List component="div" disablePadding classes={{ root: classes.listRoot }}>
                  {route.subPages.sort(routesSortFn).map((subPage) => (
                    renderSingleNav(subPage, isSelected(subPage.link), true)
                  ))}
                </List>
              </Collapse>
            </>
          )
          : renderSingleNav(route, isSelected(route.link))}
        {renderDivider()}
      </React.Fragment>
    ));
  };

  const renderExpansionIcon = (route) => openTabs[route.selectedTabId] ? <ExpandLess /> : <ExpandMore />;

  const renderDivider = () => <Divider className={classes.divider} />;

  const toggleExpandNavItems = (selectedTabId) => {
    setOpenTabs({
      ...openTabs,
      [selectedTabId]: !openTabs[selectedTabId]
    });
  };

  const renderSingleNav = (route, isActive, isChild = false, hasSubPages = false) => {
    const listItemStyle = isChild
      ? { paddingLeft: open ? 22 : 12, color: 'white' }
      : { paddingLeft: 12, color: 'white' };
    return (
      <ListItem
        key={route.selectedTabId}
        button
        style={listItemStyle}
        selected={isActive}
        classes={{ selected: classes.selectedListItem }}
        className={classes.listItem}
      >
        <Link to={route.link} className={classes.link}>
          <ListItemIcon className={`${classes.iconStyle} ${open ? '' : classes.closeIcon}`}>
            <route.icon className={classes.iconStyle} />
          </ListItemIcon>
          <ListItemText
            className={classes.parentStyle}
            inset
            primary={route.name}
            primaryTypographyProps={{ style: listItemText, noWrap: true }}
          />
        </Link>
        {hasSubPages && (
          <ListItemSecondaryAction
            onClick={() => toggleExpandNavItems(route.selectedTabId)}
            className={open ? classes.drawerSecondaryActionOpen : classes.drawerSecondaryActionClose}
          >
            <IconButton aria-label="Comments" style={{ color: 'white' }}>
              {renderExpansionIcon(route)}
            </IconButton>
          </ListItemSecondaryAction>
        )}
      </ListItem>
    );
  };

  const toggleAutoToggle = () => {
    const next = !enableAutoToggle;
    setEnableAutoToggle(next);
    // Persist only under a known per-user key (mirrors the guarded restore above).
    if (userId != null) {
      writeSidebarAutoClose(userId, next);
    }
  };

  const handleMouseEnter = () => {
    if (enableAutoToggle && !open) {
      onOpenDrawer();
    }
  };

  const handleMouseLeave = () => {
    if (enableAutoToggle && open) {
      onCloseDrawer();
    }
  };

  const filteredRoutes = useMemo(
    () => {
      const searchString = search.toLowerCase();
      return routes.reduce((counter, route) => {
        const subPages = route.subPages
          ? route.subPages.filter(({ name }) => name.toLowerCase().includes(searchString))
          : [];
        if (route.name.toLowerCase().includes(searchString) || subPages.length) {
          counter.push({ ...route, subPages });
        }
        return counter;
      }, []);
    },
    [routes, search]
  );

  useEffect(
    () => {
      if (search) {
        const newOpenTabs = filteredRoutes.reduce((counter, { subPages, selectedTabId }) => {
          if (subPages && subPages.length) {
            counter[selectedTabId] = true;
          }
          return counter;
        }, {});
        setOpenTabs(newOpenTabs);
      }
    },
    [search, filteredRoutes]
  );

  return (
    <Drawer
      variant="permanent"
      color="secondary"
      classes={{ paper: classNames(classes.drawerPaper, !open && classes.drawerPaperClose) }}
      open={open}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className={classes.shadowEl} />
      <div className={classes.drawerContent}>
        <Link to={isManagerMode ? '/' : '/selfService'} style={{ textDecoration: 'none' }} className={classes.toolbarLink}>
          <div className={classes.toolbar}>
            <img style={{ height: 64 }} src={cube} alt="" />
            <img style={{ height: 22 }} src={logoText} alt="" />
          </div>
        </Link>

        {isRegisterWorkOnComputerModuleEnabled && (
          <Box padding={3} display="flex" justifyContent="center">
            <WorkShiftButton userId={userId} miniLabel={!open} />
          </Box>
        )}

        {showMenuSearch && (
          <TextField
            label={I18n.t('Search')}
            value={search}
            onChange={handleSetSearch}
            variant="outlined"
            className={classes.search}
            InputLabelProps={{
              classes: { root: classes.inputLabel }
            }}
            InputProps={{
              classes: { notchedOutline: classes.inputOutline, input: classes.input }
            }}
          />
        )}
        <List className={`navDrawer navDrawer-full ${classes.navContainer}`} component="nav">
          {renderNavItems(filteredRoutes)}
        </List>
        <ListItem button className={classes.autoHideSwitch} onClick={toggleAutoToggle}>
          <Switch color="primary" onChange={() => null} checked={enableAutoToggle} />
          <ListItemText
            style={{ paddingLeft: 5, paddingRight: 0 }}
            primary={<Translate value="AutoClose" />}
            primaryTypographyProps={{ style: listItemText, noWrap: true }}
          />
        </ListItem>
      </div>
    </Drawer>
  );
}

NavigationDrawer.propTypes = {
  showMenuSearch: PropTypes.bool.isRequired,
  open: PropTypes.bool.isRequired,
  classes: PropTypes.object.isRequired,
  selectedNav: PropTypes.object,
  routes: PropTypes.array.isRequired,
  onOpenDrawer: PropTypes.func.isRequired,
  onCloseDrawer: PropTypes.func.isRequired,
  isManagerMode: PropTypes.bool.isRequired,
  userId: PropTypes.number.isRequired,
  isRegisterWorkOnComputerModuleEnabled: PropTypes.bool.isRequired
};

NavigationDrawer.defaultProps = {
  selectedNav: {}
};

const mapStateToProps = (state) => ({
  isRegisterWorkOnComputerModuleEnabled: selectIsModuleEnabled(state, 'RegisterWorkOnComputer'),
  showMenuSearch: selectShowMenuSearch(state),
  userId: selectUserId(state)
});

export default connect(mapStateToProps, null)(withStyles(styles)(NavigationDrawer));
